// Pure scoring module for the command palette. No React, no side effects —
// safe to unit-test in isolation.
//
// matchText: tokenised AND match returning per-token strength + MatchRange[].
// computeScore: combines field weight × match strength × multiplicative boosts.
// rankAndCap: sort descending by score and take the top N.

/** A matched substring range within a text string (half-open: [start, end)). */
export interface MatchRange {
  start: number;
  end: number;
}

/** Return from matchText when the query matches. */
export interface MatchResult {
  /** Maximum token strength across all query tokens. */
  strength: number;
  /** All matched ranges (one per query token, may overlap). */
  ranges: MatchRange[];
}

// ─── Boost constants ───────────────────────────────────────────────────

/** Recency boost thresholds (strict less-than comparison):
 *  ageMs < 24 h → ×2.0; < 7 d → ×1.5; < 31 d → ×1.2; else ×1.0 */
export const RECENCY_BOOST_24H = 2.0;
export const RECENCY_BOOST_7D = 1.5;
export const RECENCY_BOOST_31D = 1.2;

const MS_24H = 24 * 60 * 60 * 1000;
const MS_7D = 7 * 24 * 60 * 60 * 1000;
const MS_31D = 31 * 24 * 60 * 60 * 1000;

/** Status boost: active/open items rank higher than closed/archived. */
export const STATUS_BOOST_ACTIVE = 1.5;
export const STATUS_BOOST_INACTIVE = 1.0;

// ─── matchText ─────────────────────────────────────────────────────────

/** Per-token match strength: prefix match = 3, word-boundary match = 2,
 *  substring match = 1. These cover "mil pay" / "qitc 104"-style queries
 *  where tokens can land anywhere in the text. */
function tokenStrength(text: string, tokenLower: string): { strength: number; start: number } | null {
  const textLower = text.toLowerCase();
  const pos = textLower.indexOf(tokenLower);
  if (pos === -1) return null;

  if (pos === 0) return { strength: 3, start: pos };

  // Word boundary: preceded by a non-word character (space, hyphen, etc.)
  const prevChar = textLower[pos - 1];
  if (/\W/u.test(prevChar)) return { strength: 2, start: pos };

  return { strength: 1, start: pos };
}

/**
 * Tokenised AND match. Splits `query` on whitespace; every token must
 * appear somewhere in `text`. Returns the max per-token strength and the
 * union of all matched ranges, or null if any token misses.
 */
export function matchText(text: string, query: string): MatchResult | null {
  const trimmed = query.trim();
  if (!trimmed) return null;

  const tokens = trimmed.split(/\s+/u).filter(Boolean);
  const ranges: MatchRange[] = [];
  let maxStrength = 0;

  for (const token of tokens) {
    const result = tokenStrength(text, token.toLowerCase());
    if (!result) return null; // AND: any miss → no match
    if (result.strength > maxStrength) maxStrength = result.strength;
    ranges.push({ start: result.start, end: result.start + token.length });
  }

  return { strength: maxStrength, ranges };
}

// ─── computeScore ──────────────────────────────────────────────────────

/** Determines whether a status string maps to the active/open tier. */
function isActiveStatus(status: string): boolean {
  // Managed contract active states; draft contracts are always "active" tier
  return status === "active" || status === "open" || status === "draft" || status === "generated";
}

function recencyBoost(ageMs: number): number {
  if (ageMs < MS_24H) return RECENCY_BOOST_24H;
  if (ageMs < MS_7D) return RECENCY_BOOST_7D;
  if (ageMs < MS_31D) return RECENCY_BOOST_31D;
  return 1.0;
}

/**
 * Computes the final candidate score:
 *   fieldWeight × matchStrength × recencyBoost × statusBoost
 *
 * `updatedAt` and optional `lastVisitedAt` are ISO strings; the more
 * recent of the two drives the recency boost so palette recents feed
 * back into ranking.
 *
 * @param fieldWeight   The weight for the matched field (title ×3, ref ×2, supplier ×1.5)
 * @param matchStrength The strength returned by matchText (1–3)
 * @param updatedAt     ISO timestamp from the domain object
 * @param lastVisitedAt ISO timestamp from recents, or null
 * @param status        The candidate's status string
 * @param nowMs         Epoch ms for "now" — injected so tests can freeze time
 */
export function computeScore(
  fieldWeight: number,
  matchStrength: number,
  updatedAt: string,
  lastVisitedAt: string | null,
  status: string,
  nowMs: number = Date.now(),
): number {
  const updatedMs = new Date(updatedAt).getTime();
  const visitedMs = lastVisitedAt ? new Date(lastVisitedAt).getTime() : -Infinity;
  const mostRecentMs = Math.max(updatedMs, visitedMs);
  const ageMs = nowMs - mostRecentMs;

  const rb = recencyBoost(ageMs);
  const sb = isActiveStatus(status) ? STATUS_BOOST_ACTIVE : STATUS_BOOST_INACTIVE;

  return fieldWeight * matchStrength * rb * sb;
}

// ─── rankAndCap ────────────────────────────────────────────────────────

/** Sort items descending by their `score` property and return the top `cap`. */
export function rankAndCap<T extends { score: number }>(items: T[], cap: number): T[] {
  return [...items].sort((a, b) => b.score - a.score).slice(0, cap);
}
